import { useId } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, Typography } from "@foodpilot/foods";
import { FacetValueList } from "./FacetValueList";
import { constrainsNothing } from "./facetSelection";
import type { FacetDimension, FacetPanelLabels, FacetSelection } from "./types";

type FacetDimensionRowProps = {
    dimension: FacetDimension;
    labels: FacetPanelLabels;
    isCollapsible: boolean;
    isExpanded: boolean;
    onToggleExpanded: () => void;
    showAllValues: boolean;
    onToggleShowAllValues: () => void;
    valueFilterQuery: string;
    onValueFilterQueryChange: (query: string) => void;
    onSelectionChange: (selection: FacetSelection) => void;
};

export const FacetDimensionRow = (props: FacetDimensionRowProps) => {
    const {
        dimension,
        labels,
        isCollapsible,
        isExpanded,
        onToggleExpanded,
        showAllValues,
        onToggleShowAllValues,
        valueFilterQuery,
        onValueFilterQueryChange,
        onSelectionChange,
    } = props;
    const theme = useTheme();
    const valueListId = useId();
    const isFiltering = !constrainsNothing(dimension.selection);

    const header = (
        <Stack direction="row" alignItems="center" gap={theme.spacing(0.5)} width="100%">
            {isCollapsible && <FoodsIcon icon={isExpanded ? "arrowDownShort" : "arrowRightShort"} size={2} />}
            <Typography variant="small-semibold" noWrap flexGrow={1} minWidth={0} textAlign="left" lineHeight={1.35}>
                {dimension.label}
            </Typography>
            {isFiltering && (
                <Box component="span" flexShrink={0} sx={{ color: "text.secondary", display: "inline-flex" }}>
                    <FoodsIcon icon="checkCircleFilled" size={2} />
                </Box>
            )}
        </Stack>
    );

    return (
        <Stack>
            {isCollapsible ?
                <Button
                    variant="black-text"
                    fullWidth
                    onClick={onToggleExpanded}
                    aria-expanded={isExpanded}
                    aria-controls={isExpanded ? valueListId : undefined}
                    sx={{
                        borderRadius: theme.spacing(0.5),
                        padding: theme.spacing(0.25),
                        minWidth: "auto",
                        height: "auto",
                        "&:hover": { backgroundColor: theme.palette.grey[400] },
                    }}
                >
                    {header}
                </Button>
            :   <Stack padding={theme.spacing(0.25)}>{header}</Stack>}
            {isExpanded && (
                <FacetValueList
                    id={valueListId}
                    values={dimension.values}
                    domain={dimension.domain}
                    selection={dimension.selection}
                    labels={labels}
                    showAllValues={showAllValues}
                    onToggleShowAllValues={onToggleShowAllValues}
                    valueFilterQuery={valueFilterQuery}
                    onValueFilterQueryChange={onValueFilterQueryChange}
                    onSelectionChange={onSelectionChange}
                />
            )}
        </Stack>
    );
};
